import { Activity, useCallback, useEffect, useState } from 'react';

import useFetchProjectGroups from '@/micros/choose-template/choose-template/components/choose-template-panel/use-fetch-project-groups';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  getWorkingProjectUuid,
  selectProjectGlobalTemplateType,
  selectProjectThemePack,
  selectWorkingProjectImage,
  selectWorkingProjectStatus,
} from '@/store/slices/project/selectors';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import Modal from '@ui/modal/modal';
import Text from '@ui/text/text/text';
import ImageUpload from '@ui/image-upload/image-upload';
import Container from '@ui/container/container';
import Checkbox from '@ui/checkbox/checkbox';
import { ValidateProjectName } from '@/connection/projects/validate-project-name';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { CreateTemplateProject } from '@/connection/create-template-project';
import { UpdateThemePackAsGlobal } from '@/connection/theme-packs';
import toastTrigger from '@ui/toast/toast-trigger';
import { assertReactSelectOptions } from '@/utils/assertions/assert-react-select-options';
import FolderSelect from '../controls/folder-select';
import { BORDER_DASH_GRAY_400 } from '@/micros/edit-project/helpers/constants/border-constants';
import { EProjectStatus, GetEventsResponse } from '@/types/projects.types';
import SaveTemplateConfirmationModal from './confirmation-modal';
import Button from '@ui/buttons/button/button';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EToastTypes } from '@ui/toast/toast.types';
import GlobalTemplatesChannelGuard from '@ui/auth-wrapper/global-templates-channel-guard';
import isGlobalTemplatesActiveChannel from '@ui/auth-wrapper/helpers/is-global-templates-active-channel';
import isGlobalThemePack from '@/utils/is-global-theme-pack';
import GlobalTemplateTypeSelector from '@ui/global-templates/global-template-type-selector';
import { selectFontPacks } from '@/store/slices/templates/selectors';
import { setFontPack, setThemePack } from '@/store/slices/templates';

type SaveAsTemplateModalProps = {
  open: boolean;
  handleClose: () => void;
};

const SaveAsTemplateModal = ({ open, handleClose }: SaveAsTemplateModalProps) => {
  const dispatch = useAppDispatch();
  const channel = useAppSelector(getActiveChannel);
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const projectStatus = useAppSelector(selectWorkingProjectStatus);
  const workingProjectImage = useAppSelector(selectWorkingProjectImage);
  const workingProjectGlobalTemplateType = useAppSelector(selectProjectGlobalTemplateType);
  const themePack = useAppSelector(selectProjectThemePack);
  const fontPacks = useAppSelector(selectFontPacks);

  const isGlobalChannel = isGlobalTemplatesActiveChannel(channel);
  const themePackIsGlobal = isGlobalThemePack(themePack);
  const showMarkThemepackAsGlobal = isGlobalChannel && !!themePack && !themePackIsGlobal;

  const [dirty, setDirty] = useState({ name: false, globalTemplateType: false });
  const [nameExistsError, setNameExistsError] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedProjectFolders, setSelectedProjectFolders] = useState<{ label: string; value: string }[]>([]);
  const [uploadedImage, setUploadedImage] = useState<string | null | undefined>(workingProjectImage);
  const [globalTemplateType, setGlobalTemplateType] = useState<string>(workingProjectGlobalTemplateType || '');
  const [markThemepackAsGlobal, setMarkThemepackAsGlobal] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [template, setTemplate] = useState<GetEventsResponse | null>(null);

  const isPublished = projectStatus === EProjectStatus.PUBLIC;
  const missingGlobalTemplateType = isGlobalChannel && !globalTemplateType.trim();

  /* `skip: !open` so we don't bother fetching until we actually need to */
  const { loading: loadingProjectFolders } = useFetchProjectGroups({ skip: !open });

  const onClose = useCallback(() => {
    if (saving) return;
    handleClose();
  }, [handleClose, saving]);

  const handleSave = useCallback(async () => {
    if (saving || !channel || !projectUuid) return;
    try {
      setSaving(true);

      const _name = name.trim();
      const _description = description.trim();
      const _globalTemplateType = globalTemplateType.trim();

      if (!_name) {
        setDirty((prev) => ({ ...prev, name: true }));
        return;
      }

      if (isGlobalChannel && !_globalTemplateType) {
        setDirty((prev) => ({ ...prev, globalTemplateType: true }));
        return;
      }

      const nameExists = await ValidateProjectName({
        projectName: _name,
        channel,
        eventId: 0,
      });
      if (nameExists.exists) {
        setNameExistsError(true);
        return;
      }

      if (isGlobalChannel && markThemepackAsGlobal && themePack && !isGlobalThemePack(themePack)) {
        try {
          await UpdateThemePackAsGlobal({
            themePackId: themePack.color_pack,
            isGlobal: true,
          });

          dispatch(
            setThemePack({
              ...themePack,
              channel: 0,
              creation_type: null,
            })
          );

          const matchedFontPack = fontPacks?.find((fp) => fp.font_pack === themePack.font_pack?.font_pack);
          if (matchedFontPack) {
            dispatch(setFontPack({ ...matchedFontPack, channel: 0 }));
          }
        } catch (themePackError) {
          console.error(themePackError);
          toastTrigger('Failed to mark theme pack as global. Template not saved.', {
            type: EToastTypes.error,
            autoClose: 10000,
            description: 'Refresh and try again',
          });
          return;
        }
      }

      const result = await CreateTemplateProject({
        channel,
        projectUuid,
        templateInfo: {
          name: _name,
          description: _description,
          groups: selectedProjectFolders.map((pg) => pg.value),
          event_image: uploadedImage || '',
          global_template_type: _globalTemplateType || null,
        },
      });
      setTemplate(result);
      setShowConfirmationModal(true);
    } catch (error) {
      console.error(error);
      toastTrigger('Failed to save as template', {
        type: EToastTypes.error,
        autoClose: 10000,
        description: 'Refresh and try again',
      });
    } finally {
      setSaving(false);
    }
  }, [
    channel,
    description,
    dispatch,
    fontPacks,
    globalTemplateType,
    isGlobalChannel,
    markThemepackAsGlobal,
    name,
    projectUuid,
    saving,
    selectedProjectFolders,
    themePack,
    uploadedImage,
  ]);

  // reset everything when modal closes
  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setSelectedProjectFolders([]);
      setUploadedImage(null);
      setDirty({ name: false, globalTemplateType: false });
      setGlobalTemplateType(workingProjectGlobalTemplateType || '');
      setMarkThemepackAsGlobal(true);
    } else {
      setUploadedImage(workingProjectImage);
    }
  }, [open, workingProjectGlobalTemplateType, workingProjectImage]);

  const RenderFooterContent = useCallback(() => {
    return (
      <Container
        background="transparent"
        className="flex fd-row jc-flex-end ai-center gap-8"
        padding="0 40px 24px 40px"
      >
        <Button design="LocalBttnSec" onClick={onClose} disabled={saving} className="L1 padding-v-16 padding-h-24">
          Cancel
        </Button>

        <Button
          backgroundColor={getThemeColorCSSVariable()}
          padding="16px 24px"
          width="auto"
          disabled={!name.trim() || loadingProjectFolders || saving || missingGlobalTemplateType}
          onClick={handleSave}
          loading={saving}
          hoverOpacity={0.8}
        >
          <Text color={getThemeColorContrastCSSVariable()} className="L1">
            Save
          </Text>
        </Button>
      </Container>
    );
  }, [handleSave, loadingProjectFolders, missingGlobalTemplateType, name, onClose, saving]);

  return (
    <>
      <Modal
        open={open}
        customSize={{
          height: 'auto',
          minHeight: '200px',
          width: '640px',
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 64px)',
        }}
        RenderHeaderContent={() => {
          return (
            <Container background="transparent" padding="24px 40px">
              <Text tag="h4" className="H4 margin-b-4">
                Save as template
              </Text>
              <Text className="P1" design="GlobalIcoText2">
                Set name and select what content you'd like to save
              </Text>
            </Container>
          );
        }}
        RenderFooterContent={RenderFooterContent}
      >
        <Container background="transparent" padding="0 40px 24px 40px" className="flex-column gap-16">
          <div>
            <label className="margin-b-5 margin-l-16 P2" htmlFor="template-name">
              Template name *
            </label>
            <InputWithOverlay
              status={(dirty.name && !name.trim()) || nameExistsError ? 'error' : undefined}
              typography="S2"
              designSize="large"
              inputProps={{
                id: 'template-name',
                placeholder: 'Add a name...',
                onBlur: () => setDirty((prev) => ({ ...prev, name: true })),
                onChange: (e) => {
                  setName(e.target.value);
                  setNameExistsError(false);
                },
                value: name,
                disabled: saving,
              }}
            />
            <Activity mode={nameExistsError ? 'visible' : 'hidden'}>
              <div>
                <Text className="S3 margin-l-16" color="var(--semantic-red-500)">
                  A project with this name already exists
                </Text>
              </div>
            </Activity>
          </div>

          {/* Event group */}
          <FolderSelect
            isOpen={open}
            isDisabled={saving}
            placeholder="Add template to folder(s)"
            label="Project folder(s)"
            onChange={(selected) => {
              try {
                assertReactSelectOptions<{ label: string; value: string; meta?: unknown }>(selected);
                setSelectedProjectFolders(selected);
              } catch (error) {
                console.error(error);
              }
            }}
          />

          {/* Template description */}
          <div>
            <label className="margin-b-5 margin-l-16 P2" htmlFor="template-description">
              Template description
            </label>
            <InputWithOverlay
              typography="S2"
              inputProps={{
                id: 'template-description',
                placeholder: 'Add a description...',
                onChange: (e) => setDescription(e.target.value),
                value: description,
                disabled: saving,
              }}
            />
          </div>

          {/* Global templates channel: required type + optional theme-pack-as-global */}
          <GlobalTemplatesChannelGuard>
            <GlobalTemplateTypeSelector
              bypassFetchGlobalTemplateTypes={!open}
              onChange={(value) => {
                setGlobalTemplateType(value);
                if (value.trim()) {
                  setDirty((prev) => ({ ...prev, globalTemplateType: false }));
                }
              }}
              onMenuClose={() => setDirty((prev) => ({ ...prev, globalTemplateType: true }))}
              error={
                dirty.globalTemplateType && !globalTemplateType.trim() ? 'Global Template Type is required.' : null
              }
            />

            <Activity mode={showMarkThemepackAsGlobal ? 'visible' : 'hidden'}>
              <div>
                <Text className="S3 margin-l-16 margin-b-8" design="GlobalIcoText2">
                  Marking a theme pack as global will allow it to become accessible on other channels and work as
                  expected on global templates.{' '}
                  <strong>
                    <u>Highly recommended.</u>
                  </strong>
                </Text>
                <Checkbox
                  value="mark-themepack-as-global"
                  label="Make theme pack a global pack?"
                  checked={markThemepackAsGlobal}
                  onChange={(_, isOn) => setMarkThemepackAsGlobal(isOn)}
                  disabled={saving}
                />
              </div>
            </Activity>
          </GlobalTemplatesChannelGuard>

          {/* Image */}
          <Container
            height="110px"
            minHeight="110px"
            width="560px"
            maxWidth="100%"
            background="transparent"
            className="margin-0-auto margin-t-12"
          >
            <ImageUpload
              onUpload={(url) => {
                if (saving) return Promise.resolve();
                setUploadedImage(url);
                return Promise.resolve();
              }}
              img={uploadedImage || BORDER_DASH_GRAY_400}
              maxSizeInBytes={10 * 1024 * 1024}
              title="Include an optional image"
              subtitle="Allowed file types are .jpg, .png, and .gif, and up to 10MB"
            />
          </Container>
        </Container>
      </Modal>
      <SaveTemplateConfirmationModal
        isOpen={showConfirmationModal}
        isPublished={isPublished}
        template={template}
        onClose={() => {
          setShowConfirmationModal(false);
          onClose();
        }}
      />
    </>
  );
};

export default SaveAsTemplateModal;
